// DebugLogger.js - Multi-purpose debug logging system
class DebugLogger {
  constructor() {
    this.logs = [];
    this.maxLogs = 1000; // Prevent memory issues
    this.isEnabled = process.env.NODE_ENV === 'development';
    this.logLevels = {
      ERROR: { level: 0, emoji: '❌', color: '#ff4444' },
      WARN: { level: 1, emoji: '⚠️', color: '#ffaa00' },
      INFO: { level: 2, emoji: 'ℹ️', color: '#0099ff' },
      DEBUG: { level: 3, emoji: '🐛', color: '#9966ff' },
      TRACE: { level: 4, emoji: '🔍', color: '#666666' },
      PERFORMANCE: { level: 2, emoji: '⚡', color: '#ff6600' },
      NETWORK: { level: 2, emoji: '🌐', color: '#00cc66' },
      STATE: { level: 3, emoji: '📊', color: '#cc0099' },
      COMPONENT: { level: 3, emoji: '⚛️', color: '#61dafb' }
    };
    this.currentLogLevel = 4; // Show all by default in development
    this.originalConsole = {};
    this.interceptConsole();
    this.performanceMarks = new Map();
  }

  // Intercept console methods to capture all console outputs
  interceptConsole() {
    if (!this.isEnabled) return;

    ['log', 'warn', 'error', 'info', 'debug'].forEach(method => {
      this.originalConsole[method] = console[method];
      console[method] = (...args) => {
        // Call original console method
        this.originalConsole[method](...args);
        
        // Log to our system
        const level = method === 'log' ? 'INFO' : method.toUpperCase();
        this.log(level, `Console.${method}`, args);
      };
    });
  }

  // Restore original console methods
  restoreConsole() {
    Object.keys(this.originalConsole).forEach(method => {
      console[method] = this.originalConsole[method];
    });
  }

  // Enhanced logging with multiple purposes
  log(level = 'INFO', message, data = null, category = null) {
    if (!this.isEnabled) return;

    const levelInfo = this.logLevels[level] || this.logLevels.INFO;
    if (levelInfo.level > this.currentLogLevel) return;

    const timestamp = new Date().toISOString();
    const logEntry = {
      id: Date.now() + Math.random(),
      timestamp,
      level,
      category: category || this.detectCategory(message, data),
      message: this.formatMessage(message),
      data: this.formatData(data),
      stackTrace: level === 'ERROR' ? this.getStackTrace() : null,
      emoji: levelInfo.emoji,
      color: levelInfo.color
    };

    this.logs.push(logEntry);

    // Keep only last N logs to prevent memory issues
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Also log to original console for immediate feedback
    this.originalConsole.log(`${levelInfo.emoji} [${level}${category ? ':' + category : ''}] ${message}`, data || '');
  }

  // Helper methods for enhanced formatting
  formatMessage(message) {
    if (typeof message === 'string') return message;
    if (typeof message === 'object') return JSON.stringify(message, null, 2);
    return String(message);
  }

  formatData(data) {
    if (!data) return null;
    
    try {
      // Handle different data types
      if (typeof data === 'function') {
        return `[Function: ${data.name || 'anonymous'}]`;
      }
      
      if (data instanceof Error) {
        return {
          name: data.name,
          message: data.message,
          stack: data.stack
        };
      }
      
      if (data instanceof HTMLElement) {
        return `[HTMLElement: ${data.tagName}${data.id ? '#' + data.id : ''}${data.className ? '.' + data.className.split(' ').join('.') : ''}]`;
      }
      
      if (data instanceof Date) {
        return data.toISOString();
      }
      
      // Deep clone to avoid reference issues
      return JSON.parse(JSON.stringify(data));
    } catch (e) {
      return `[Unserializable Object: ${typeof data}]`;
    }
  }

  detectCategory(message, data) {
    const messageStr = String(message).toLowerCase();
    
    if (messageStr.includes('validation') || messageStr.includes('validate')) return 'VALIDATION';
    if (messageStr.includes('api') || messageStr.includes('fetch') || messageStr.includes('request')) return 'NETWORK';
    if (messageStr.includes('render') || messageStr.includes('component') || messageStr.includes('mount')) return 'COMPONENT';
    if (messageStr.includes('state') || messageStr.includes('redux') || messageStr.includes('context')) return 'STATE';
    if (messageStr.includes('performance') || messageStr.includes('time') || messageStr.includes('speed')) return 'PERFORMANCE';
    if (messageStr.includes('error') || messageStr.includes('exception') || messageStr.includes('failed')) return 'ERROR';
    if (messageStr.includes('auth') || messageStr.includes('login') || messageStr.includes('token')) return 'AUTH';
    if (messageStr.includes('form') || messageStr.includes('input') || messageStr.includes('submit')) return 'FORM';
    
    return 'GENERAL';
  }

  getStackTrace() {
    try {
      throw new Error();
    } catch (e) {
      return e.stack;
    }
  }

  // Convenience methods for different log levels
  error(message, data = null, category = null) {
    this.log('ERROR', message, data, category);
  }

  warn(message, data = null, category = null) {
    this.log('WARN', message, data, category);
  }

  info(message, data = null, category = null) {
    this.log('INFO', message, data, category);
  }

  debug(message, data = null, category = null) {
    this.log('DEBUG', message, data, category);
  }

  trace(message, data = null, category = null) {
    this.log('TRACE', message, data, category);
  }

  // Performance monitoring
  startPerformanceMark(name) {
    this.performanceMarks.set(name, {
      startTime: performance.now(),
      startMemory: performance.memory ? performance.memory.usedJSHeapSize : null
    });
    this.log('PERFORMANCE', `Started timing: ${name}`);
  }

  endPerformanceMark(name) {
    const mark = this.performanceMarks.get(name);
    if (!mark) {
      this.warn('Performance mark not found', { name });
      return null;
    }

    const duration = performance.now() - mark.startTime;
    const memoryUsed = performance.memory ? 
      performance.memory.usedJSHeapSize - (mark.startMemory || 0) : null;

    const result = {
      name,
      duration: `${duration.toFixed(2)}ms`,
      memoryDelta: memoryUsed ? `${(memoryUsed / 1024 / 1024).toFixed(2)}MB` : 'N/A'
    };

    this.log('PERFORMANCE', `Completed timing: ${name}`, result);
    this.performanceMarks.delete(name);
    return result;
  }

  // Network request logging
  logNetworkRequest(method, url, requestData = null) {
    this.log('NETWORK', `${method.toUpperCase()} ${url}`, {
      method,
      url,
      requestData,
      timestamp: new Date().toISOString()
    }, 'REQUEST');
  }

  logNetworkResponse(method, url, status, responseData = null, duration = null) {
    this.log('NETWORK', `${method.toUpperCase()} ${url} - ${status}`, {
      method,
      url,
      status,
      responseData,
      duration: duration ? `${duration}ms` : null,
      timestamp: new Date().toISOString()
    }, 'RESPONSE');
  }

  // Component lifecycle logging
  logComponentMount(componentName, props = null) {
    this.log('COMPONENT', `${componentName} mounted`, { props }, 'LIFECYCLE');
  }

  logComponentUnmount(componentName) {
    this.log('COMPONENT', `${componentName} unmounted`, null, 'LIFECYCLE');
  }

  logComponentUpdate(componentName, prevProps = null, nextProps = null) {
    this.log('COMPONENT', `${componentName} updated`, { prevProps, nextProps }, 'LIFECYCLE');
  }

  // State change logging
  logStateChange(stateName, prevState, nextState, action = null) {
    this.log('STATE', `State change: ${stateName}`, {
      previous: prevState,
      next: nextState,
      action
    });
  }

  // Form validation logging
  logValidation(fieldName, value, isValid, errors = null) {
    this.log('DEBUG', `Validation: ${fieldName}`, {
      fieldName,
      value,
      isValid,
      errors
    }, 'VALIDATION');
  }

  // Enhanced text output with filtering
  getLogsAsText(filters = {}) {
    let filteredLogs = this.logs;

    // Apply filters
    if (filters.level) {
      filteredLogs = filteredLogs.filter(log => log.level === filters.level);
    }
    if (filters.category) {
      filteredLogs = filteredLogs.filter(log => log.category === filters.category);
    }
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filteredLogs = filteredLogs.filter(log => 
        log.message.toLowerCase().includes(searchTerm) ||
        (log.data && JSON.stringify(log.data).toLowerCase().includes(searchTerm))
      );
    }
    if (filters.timeRange) {
      const { start, end } = filters.timeRange;
      filteredLogs = filteredLogs.filter(log => {
        const logTime = new Date(log.timestamp);
        return logTime >= start && logTime <= end;
      });
    }

    return filteredLogs.map(log => {
      const dataStr = log.data ? '\n' + JSON.stringify(log.data, null, 2) : '';
      const stackStr = log.stackTrace ? '\nStack Trace:\n' + log.stackTrace : '';
      return `${log.emoji} [${log.timestamp}] [${log.level}${log.category ? ':' + log.category : ''}]\n${log.message}${dataStr}${stackStr}\n${'='.repeat(80)}\n`;
    }).join('\n');
  }

  // Enhanced HTML output for better readability
  getLogsAsHTML(filters = {}) {
    let filteredLogs = this.logs;

    // Apply same filters as text version
    if (filters.level) {
      filteredLogs = filteredLogs.filter(log => log.level === filters.level);
    }
    if (filters.category) {
      filteredLogs = filteredLogs.filter(log => log.category === filters.category);
    }
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filteredLogs = filteredLogs.filter(log => 
        log.message.toLowerCase().includes(searchTerm) ||
        (log.data && JSON.stringify(log.data).toLowerCase().includes(searchTerm))
      );
    }

    const html = `
      <style>
        .debug-log { font-family: 'Courier New', monospace; margin: 10px 0; padding: 10px; border-radius: 4px; }
        .debug-header { font-weight: bold; margin-bottom: 5px; }
        .debug-data { background: #f5f5f5; padding: 8px; border-radius: 4px; white-space: pre-wrap; }
        .debug-stack { background: #ffe6e6; padding: 8px; border-radius: 4px; font-size: 12px; }
      </style>
      <div class="debug-logs">
        ${filteredLogs.map(log => `
          <div class="debug-log" style="border-left: 4px solid ${log.color}">
            <div class="debug-header" style="color: ${log.color}">
              ${log.emoji} [${new Date(log.timestamp).toLocaleString()}] [${log.level}${log.category ? ':' + log.category : ''}]
            </div>
            <div class="debug-message">${log.message}</div>
            ${log.data ? `<div class="debug-data">${JSON.stringify(log.data, null, 2)}</div>` : ''}
            ${log.stackTrace ? `<div class="debug-stack">${log.stackTrace}</div>` : ''}
          </div>
        `).join('')}
      </div>
    `;
    
    return html;
  }

  // Download logs with options
  downloadLogs(filename = 'debug-logs.txt', format = 'text', filters = {}) {
    if (!this.isEnabled) {
      console.log('Debug logging is disabled in production');
      return;
    }

    let content, mimeType;
    
    if (format === 'html') {
      content = this.getLogsAsHTML(filters);
      mimeType = 'text/html';
      filename = filename.replace('.txt', '.html');
    } else if (format === 'json') {
      content = JSON.stringify(this.logs, null, 2);
      mimeType = 'application/json';
      filename = filename.replace('.txt', '.json');
    } else {
      content = this.getLogsAsText(filters);
      mimeType = 'text/plain';
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Enhanced search and filtering
  search(query, options = {}) {
    const { level, category, timeRange } = options;
    const searchTerm = query.toLowerCase();
    
    return this.logs.filter(log => {
      const matchesSearch = log.message.toLowerCase().includes(searchTerm) ||
        (log.data && JSON.stringify(log.data).toLowerCase().includes(searchTerm));
      
      const matchesLevel = !level || log.level === level;
      const matchesCategory = !category || log.category === category;
      const matchesTime = !timeRange || (
        new Date(log.timestamp) >= timeRange.start &&
        new Date(log.timestamp) <= timeRange.end
      );
      
      return matchesSearch && matchesLevel && matchesCategory && matchesTime;
    });
  }

  // Get statistics
  getStats() {
    const stats = {
      total: this.logs.length,
      byLevel: {},
      byCategory: {},
      timeRange: {
        start: this.logs.length > 0 ? this.logs[0].timestamp : null,
        end: this.logs.length > 0 ? this.logs[this.logs.length - 1].timestamp : null
      }
    };

    this.logs.forEach(log => {
      stats.byLevel[log.level] = (stats.byLevel[log.level] || 0) + 1;
      stats.byCategory[log.category] = (stats.byCategory[log.category] || 0) + 1;
    });

    return stats;
  }

  // Set log level
  setLogLevel(level) {
    const levelNames = Object.keys(this.logLevels);
    if (levelNames.includes(level)) {
      this.currentLogLevel = this.logLevels[level].level;
    } else if (typeof level === 'number' && level >= 0 && level <= 4) {
      this.currentLogLevel = level;
    }
  }

  // Save to localStorage with compression
  saveToStorage() {
    if (!this.isEnabled) return;
    
    try {
      const dataToSave = {
        logs: this.logs,
        stats: this.getStats(),
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('debug-logs', JSON.stringify(dataToSave));
    } catch (e) {
      console.warn('Could not save debug logs to localStorage:', e);
    }
  }

  // Load from localStorage
  loadFromStorage() {
    if (!this.isEnabled) return;
    
    try {
      const saved = localStorage.getItem('debug-logs');
      if (saved) {
        const data = JSON.parse(saved);
        this.logs = data.logs || data; // Support old format
      }
    } catch (e) {
      console.warn('Could not load debug logs from localStorage:', e);
    }
  }

  // Clear all logs
  clear() {
    this.logs = [];
    this.performanceMarks.clear();
    localStorage.removeItem('debug-logs');
    this.info('Debug logs cleared');
  }

  // Export logs to console as text (for copying)
  exportToConsole(filters = {}) {
    console.log('=== ENHANCED DEBUG LOGS ===');
    console.log(this.getLogsAsText(filters));
    console.log('=== END DEBUG LOGS ===');
    console.log('Stats:', this.getStats());
  }
}

// Create singleton instance
const debugLogger = new DebugLogger();

// Load any existing logs on startup
debugLogger.loadFromStorage();

// Auto-save every 30 seconds
setInterval(() => {
  debugLogger.saveToStorage();
}, 30000);

// Make it globally available for debugging
if (typeof window !== 'undefined') {
  window.debugLogger = debugLogger;
}

export default debugLogger;
